import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const EnterPage = ({ onEnter }) => {
  const [isReady, setIsReady] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setIsReady(true), 300);
    
    // Generate particles
    const newParticles = [];
    for (let i = 0; i < 50; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 1,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        delay: Math.random() * 3
      });
    }
    setParticles(newParticles);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ 
        x: (e.clientX / window.innerWidth - 0.5) * 30,
        y: (e.clientY / window.innerHeight - 0.5) * 30
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="fixed inset-0 bg-black z-40 overflow-hidden">
      {/* Animated gradient background */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 50% 50%, #ffffff 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite'
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-white"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              animation: `float-particle 20s linear infinite`,
              animationDelay: `${particle.delay}s`,
              opacity: 0.4
            }}
          />
        ))}
      </div>

      {/* Rotating geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 border-2 border-white/10"
          style={{
            animation: 'rotate-slow 20s linear infinite',
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`
          }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-64 h-64 border border-white/10 rounded-full"
          style={{
            animation: 'rotate-slow 15s linear infinite reverse',
            transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`
          }}
        />
        <div 
          className="absolute top-1/2 left-1/2 w-80 h-80 border-2 border-white/5 transform -translate-x-1/2 -translate-y-1/2"
          style={{
            clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
            animation: 'rotate-slow 25s linear infinite'
          }}
        />
      </div>

      {/* Scanline effect */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 2px, transparent 2px, transparent 4px)',
          animation: 'scanline 10s linear infinite'
        }}
      />

      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center px-6">
        {/* Logo with crazy 3D effect */}
        <div 
          className={`mb-16 transition-all duration-1500 ${isReady ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
          style={{
            transform: `perspective(1000px) rotateX(${-mousePosition.y * 0.5}deg) rotateY(${mousePosition.x * 0.5}deg) translateZ(50px)`,
            transformStyle: 'preserve-3d',
            transitionDelay: '0.2s'
          }}
        >
          <div className="relative">
            {/* Glowing rings */}
            <div className="absolute inset-0 -m-20">
              <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-ping-slow" />
              <div className="absolute inset-4 rounded-full border border-white/10 animate-ping-slow" style={{ animationDelay: '1s' }} />
              <div className="absolute inset-8 rounded-full border border-white/5 animate-ping-slow" style={{ animationDelay: '2s' }} />
            </div>

            {/* Logo container with 3D transform */}
            <div className="relative w-56 h-56 flex items-center justify-center">
              <div 
                className="absolute inset-0 bg-white/5 rounded-full blur-3xl animate-pulse"
                style={{ animationDuration: '3s' }}
              />
              <img 
                src="/cortexa-logo.jpeg" 
                alt="Cortexa" 
                className="relative w-40 h-40 object-contain z-10"
                style={{
                  filter: 'drop-shadow(0 0 60px rgba(255,255,255,0.5)) brightness(1.2) contrast(1.2)',
                  animation: 'float-gentle 4s ease-in-out infinite'
                }}
              />
            </div>
          </div>
        </div>

        {/* Text with glitch effect */}
        <div 
          className={`text-center mb-16 transition-all duration-1500 ${isReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
          style={{ transitionDelay: '0.4s' }}
        >
          <h1 
            className="text-8xl md:text-9xl font-light mb-8 text-white relative"
            style={{ 
              fontFamily: 'Georgia, serif',
              letterSpacing: '0.3em',
              textShadow: '0 0 30px rgba(255,255,255,0.5), 0 0 60px rgba(255,255,255,0.3)',
              animation: 'glitch 5s infinite'
            }}
          >
            CORTEXA
          </h1>
          
          <div className="overflow-hidden mb-8">
            <div 
              className="h-px bg-white"
              style={{
                animation: 'line-expand 2s ease-out forwards',
                transformOrigin: 'center',
                boxShadow: '0 0 20px rgba(255,255,255,0.8)'
              }}
            />
          </div>
          
          <p className="text-base md:text-xl text-white/70 tracking-[0.4em] uppercase animate-pulse">
            AI Marketing Intelligence
          </p>
        </div>

        {/* Button with crazy hover effect */}
        <div
          className={`transition-all duration-1500 ${isReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
          style={{ transitionDelay: '0.6s' }}
        >
          <button
            onClick={onEnter}
            className="group relative px-20 py-6 border-2 border-white overflow-hidden transition-all duration-500 hover:scale-110"
            style={{
              boxShadow: '0 0 30px rgba(255,255,255,0.3)',
              transformStyle: 'preserve-3d'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 60px rgba(255,255,255,0.8), inset 0 0 60px rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 0 30px rgba(255,255,255,0.3)';
            }}
          >
            {/* Animated background */}
            <div 
              className="absolute inset-0 bg-white transition-transform duration-500 group-hover:scale-100 scale-0"
            />
            
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white transition-all duration-300 group-hover:w-12 group-hover:h-12 group-hover:-translate-x-1 group-hover:-translate-y-1" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white transition-all duration-300 group-hover:w-12 group-hover:h-12 group-hover:translate-x-1 group-hover:translate-y-1" />
            
            <span className="relative z-10 flex items-center gap-4 text-lg tracking-[0.3em] text-white group-hover:text-black transition-colors duration-500 font-light">
              <span>ENTER</span>
              <ArrowRight className="w-6 h-6 transition-transform duration-500 group-hover:translate-x-2 group-hover:scale-125" />
            </span>
          </button>
        </div>

        {/* Bottom indicator */}
        <div 
          className={`absolute bottom-16 transition-all duration-1500 ${isReady ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: '0.8s' }}
        >
          <div className="text-xs tracking-[0.5em] text-white/30 uppercase mb-4">
            Scroll to Explore
          </div>
          <div className="flex justify-center">
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
              <div 
                className="w-1 h-2 bg-white rounded-full"
                style={{
                  animation: 'scroll-bounce 2s ease-in-out infinite'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float-gentle {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes rotate-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.3; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes line-expand {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes scroll-bounce {
          0%, 100% { transform: translateY(0px); opacity: 1; }
          50% { transform: translateY(10px); opacity: 0.3; }
        }
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes float-particle {
          0% { transform: translate(0, 0); }
          25% { transform: translate(100px, -100px); }
          50% { transform: translate(-50px, -200px); }
          75% { transform: translate(-150px, -100px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes glitch {
          0%, 90%, 100% { transform: translate(0); }
          91% { transform: translate(-2px, 2px); }
          92% { transform: translate(2px, -2px); }
          93% { transform: translate(-2px, 2px); }
          94% { transform: translate(2px, -2px); }
        }
        .animate-ping-slow {
          animation: ping-slow 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default EnterPage;
